import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Form, message } from 'antd';
import { StyledDrawer, StyledFooter, StyledDrawerTitle } from './style';
import { Button } from '../../Button';
import { TextArea } from '../../TextArea';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { Label } from 'ui/Label';
import EnumSelect from '../../../admin/Selects/EnumSelect';

interface DrawerWithdrawListingApplicationProps {
  open: boolean;
  onClose: () => void;
  applicationId: number;
  onSuccess?: () => void;
}

interface WithdrawValues {
  cancel_reason: string;
  cancel_reason_description?: string;
}

const ALLOWED_WITHDRAW_TYPES = [
  'cancelled_not_actual',
  'cancelled_unexpected',
  'cancelled_products_not_available',
  'cancelled_other',
] as const;

export const DrawerWithdrawListingApplication = ({
  open,
  onClose,
  applicationId,
  onSuccess,
}: DrawerWithdrawListingApplicationProps) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const [selectedCancelType, setSelectedCancelType] = useState<string>('');

  const { data: applicationData } = useQueryApiClient({
    request: {
      url: `/api/v2/listing-applications/${applicationId}`,
    },
    enabled: !!applicationId && open,
  });

  const { appendData: withdrawApplication, isLoading: isWithdrawing } =
    useQueryApiClient({
      request: {
        url: `/api/v2/listing-applications/:id/action`,
        method: 'POST',
      },
      onSuccess: () => {
        message.success(
          intl.formatMessage({ id: 'listings.applications.withdraw_success' })
        );
        handleClose();
        onSuccess?.();
      },
      onError: () => {
        message.error(
          intl.formatMessage({ id: 'listings.applications.withdraw_error' })
        );
      },
    });

  const handleCancelTypeChange = (value: string) => {
    setSelectedCancelType(value);
  };

  const handleSubmit = (values: WithdrawValues) => {
    withdrawApplication(
      {
        ...values,
        action: 'withdraw',
      },
      { id: applicationId }
    );
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedCancelType('');
    onClose();
  };

  const isOtherTypeSelected = selectedCancelType === 'cancelled_other';

  return (
    <StyledDrawer
      open={open}
      onClose={handleClose}
      title={
        <StyledDrawerTitle>
          <Label
            label={intl.formatMessage({
              id: 'listings.applications.withdraw_title',
            })}
          />
          <Label label={applicationData?.listing_title || ''} title={true} />
        </StyledDrawerTitle>
      }
      width="50%"
      destroyOnClose
      className="styled-drawer"
      footer={
        <StyledFooter>
          <Button
            type="default"
            onClick={handleClose}
            disabled={isWithdrawing}
            label={intl.formatMessage({ id: 'general.back' })}
          />
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={isWithdrawing}
            label={intl.formatMessage({
              id: 'listings.applications.withdraw_confirm',
            })}
          />
        </StyledFooter>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <EnumSelect
          label={intl.formatMessage({ id: 'listings.withdraw_reason' })}
          name="cancel_reason"
          onChange={handleCancelTypeChange}
          code="ListingApplicationWithdrawType"
          limited={[...ALLOWED_WITHDRAW_TYPES]}
          validations={['required']}
        />

        {isOtherTypeSelected && (
          <TextArea
            name="cancel_reason_description"
            placeholder={intl.formatMessage({
              id: 'listings.cancel_reason_description_placeholder',
            })}
            maxLength={2000}
            validations={['required']}
          />
        )}
      </Form>
    </StyledDrawer>
  );
};
